import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import { useEffect, useRef, useState, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import { tokens } from '../../../design';

/**
 * A join code in the mockup's `.code-box`: mono value + «Скопировать».
 * `masked` renders the placeholder (the owner code is not stored) and no
 * copy button. Codes are not PII — copy needs no `view_pii`.
 */
const COPIED_MS = 1500;

export const CodeBox = ({
  label,
  code,
  masked = false,
  maskedHint,
  testId,
}: {
  label?: string;
  code: string;
  masked?: boolean;
  maskedHint?: string;
  /** `data-testid` of the value; never rendered on a masked box. */
  testId?: string;
}): ReactElement => {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);
  const timer = useRef<number | null>(null);
  useEffect(
    () => () => {
      if (timer.current !== null) window.clearTimeout(timer.current);
    },
    [],
  );

  const copy = (): void => {
    void navigator.clipboard?.writeText(code).then(() => {
      setCopied(true);
      timer.current = window.setTimeout(() => setCopied(false), COPIED_MS);
    });
  };

  return (
    <Box>
      {label ? (
        <Typography variant="caption" color="text.secondary" component="div" sx={{ mb: 0.5 }}>
          {label}
        </Typography>
      ) : null}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          p: 1.25,
          border: `1px solid ${tokens.border.default}`,
          borderRadius: '10px',
          bgcolor: tokens.surface.elevated,
        }}
      >
        <Typography
          sx={{
            flex: 1,
            fontFamily: tokens.font.mono,
            fontWeight: 700,
            fontSize: 16,
            letterSpacing: '0.5px',
            wordBreak: 'break-all',
            color: masked ? tokens.text.tertiary : tokens.text.primary,
          }}
          data-testid={masked ? undefined : testId}
        >
          {masked ? t('companies.codes.maskedValue') : code}
        </Typography>
        {masked ? (
          <Typography variant="caption" color="text.secondary">
            {maskedHint}
          </Typography>
        ) : (
          <Button size="small" variant="outlined" startIcon={<ContentCopyIcon />} onClick={copy}>
            {copied ? t('pii.copied') : t('pii.copy')}
          </Button>
        )}
      </Box>
    </Box>
  );
};
